import React from 'react';
import type { Metadata } from '@skd/shared';
import { Button } from '../ui/Button';
import { ComboBox } from '../ui/ComboBox';
import { Menu, MenuItem } from '../ui/Menu';

interface FrontPageProps {
  metadata: Metadata;
  onUpdateMetadata: (field: keyof Metadata, value: string) => void;
  onNext: () => void;
  onOpenChoices: () => void;
  canManageChoices: boolean;
  onLoadSample: () => void;
  invoiceList: string[];
  caseList: string[];
  boxList: string[];
  bodyList: string[];
  bodyHint: string;
}

export function FrontPage({
  metadata,
  onUpdateMetadata,
  onNext,
  onOpenChoices,
  canManageChoices,
  onLoadSample,
  invoiceList,
  caseList,
  boxList,
  bodyList,
  bodyHint,
}: FrontPageProps) {
  const handleInvoiceChange = (value: string) => onUpdateMetadata('invoice_no', value);
  const handleBodyChange = (value: string) => onUpdateMetadata('body_number', value.toUpperCase());

  const isOtherModel = !['e245', 'E22H', 'P145'].includes(metadata.model);

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3 border-b border-[var(--border-color)] p-4 sm:p-5">
        <div className="min-w-0">
          <h2 className="card-title">Vehicle &amp; container metadata</h2>
          <p className="mt-1 flex items-start gap-1.5 text-[0.75rem] text-[var(--text-dim)]">
            <svg className="mt-px flex-shrink-0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <span>Pick an invoice first — the body list filters to that invoice. Case and box are independent.</span>
          </p>
        </div>

        {/* Secondary utilities live in a menu, not as full-width primary buttons. */}
        <Menu
          label="Form options"
          trigger={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="5" r="1.6" fill="currentColor" stroke="none" />
              <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
              <circle cx="12" cy="19" r="1.6" fill="currentColor" stroke="none" />
            </svg>
          }
        >
          {canManageChoices && (
            <MenuItem
              onClick={onOpenChoices}
              icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>}
            >
              Manage choice options
            </MenuItem>
          )}
          <MenuItem
            onClick={onLoadSample}
            icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>}
          >
            Load sample data
          </MenuItem>
        </Menu>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); onNext(); }}
        className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-5"
      >
        <div className="form-group">
          <label className="form-label">Invoice number <span className="required">*</span></label>
          <ComboBox
            className="input-field font-mono"
            placeholder="Select or type invoice no"
            value={metadata.invoice_no}
            onChange={handleInvoiceChange}
            options={invoiceList}
            required
          />
          <span className="field-hint">Selecting an invoice loads its body numbers</span>
        </div>

        <div className="form-group">
          <label className="form-label">Body number <span className="required">*</span></label>
          <ComboBox
            className="input-field font-mono uppercase"
            placeholder="Select chassis body"
            value={metadata.body_number}
            onChange={handleBodyChange}
            options={bodyList}
            required
          />
          <span className="field-hint">{bodyHint}</span>
        </div>

        <div className="form-group sm:col-span-2">
          <label className="form-label">Vehicle model <span className="required">*</span></label>
          <div className="pill-radio-group">
            {['e245', 'E22H', 'P145', 'OTHER'].map((v) => (
              <label key={v} className="pill-radio">
                <input
                  type="radio"
                  name="model"
                  value={v}
                  checked={metadata.model === v || (v === 'OTHER' && isOtherModel)}
                  onChange={() => onUpdateMetadata('model', v)}
                />
                <span>{v}</span>
              </label>
            ))}
          </div>
          {isOtherModel && (
            <input
              type="text"
              className="input-field mt-1"
              placeholder="Specify other vehicle model"
              value={metadata.model_other}
              onChange={(e) => onUpdateMetadata('model_other', e.target.value)}
              required
            />
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Case number</label>
          <ComboBox
            className="input-field font-mono"
            placeholder="Select or type case no"
            value={metadata.case_no}
            onChange={(v) => onUpdateMetadata('case_no', v)}
            options={caseList}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Box number</label>
          <ComboBox
            className="input-field font-mono"
            placeholder="Select or type box no"
            value={metadata.box_no}
            onChange={(v) => onUpdateMetadata('box_no', v)}
            options={boxList}
          />
        </div>

        <div className="form-group sm:col-span-2">
          <label className="form-label">Container origin <span className="required">*</span></label>
          <div className="pill-radio-group">
            {['PORT', 'SSU', 'YARD', 'OTHERS'].map((v) => (
              <label key={v} className="pill-radio">
                <input
                  type="radio"
                  name="container_origins"
                  value={v}
                  checked={metadata.container_origins === v}
                  onChange={() => onUpdateMetadata('container_origins', v)}
                />
                <span>{v}</span>
              </label>
            ))}
          </div>
          {metadata.container_origins === 'OTHERS' && (
            <input
              type="text"
              className="input-field mt-1"
              placeholder="Specify other container origin"
              value={metadata.container_origins_other}
              onChange={(e) => onUpdateMetadata('container_origins_other', e.target.value)}
              required
            />
          )}
        </div>

        <div className="form-group sm:col-span-2">
          <label className="form-label">Inside container condition <span className="required">*</span></label>
          <div className="pill-radio-group">
            {['OK', 'LEAKING', 'DAMAGE', 'OTHERS'].map((v) => (
              <label key={v} className="pill-radio">
                <input
                  type="radio"
                  name="inside_container"
                  value={v}
                  checked={metadata.inside_container === v}
                  onChange={() => onUpdateMetadata('inside_container', v)}
                />
                <span>{v}</span>
              </label>
            ))}
          </div>
          {metadata.inside_container === 'OTHERS' && (
            <input
              type="text"
              className="input-field mt-1"
              placeholder="Specify other inside container condition"
              value={metadata.inside_container_other}
              onChange={(e) => onUpdateMetadata('inside_container_other', e.target.value)}
              required
            />
          )}
        </div>

        {/* Deliberately not sticky: this form is short, and a pinned bar would
            sit on top of the last fields while they are being filled in. */}
        <div className="mt-1 flex border-t border-[var(--border-color)] pt-4 sm:col-span-2 sm:justify-end">
          <Button type="submit" variant="primary" className="w-full sm:w-auto">
            Next: Section 1 (Receiving)
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
          </Button>
        </div>
      </form>
    </div>
  );
}
